import React, {
  useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { useResponsive } from 'common/context';
import './StickyHeaderRow.css';

/* The top control row of a mobile screen (Back + title, or Back + name +
   avatar) that travels down with you as you scroll, iOS 26 style.

   Usage: swap the existing row's wrapping <div> for <StickyHeaderRow> and keep
   the same className. Nothing else about the row changes, so at scroll
   position 0 it renders exactly as before: no background, no extra height.

   Once the row is actually stuck and content is passing under it, it gets
   data-scrolled="true", which fades in a soft "scroll edge" behind it
   (blur + light tint, masked so it fades out at the bottom rather than ending
   on a hard line, reaching up behind the status bar). See StickyHeaderRow.css.

   position: sticky only holds for as long as the row's PARENT is on screen,
   so the row must sit directly in the tall page wrapper, not in a short
   header box. It also breaks under any ancestor with overflow other than
   visible/clip, or a transform.

   Mobile only. On desktop the DesktopHeader is already sticky, so this is a
   plain div. Where IntersectionObserver is missing (jsdom, very old webviews)
   the row still sticks, it just never gets the scrolled treatment. */

type Props = React.HTMLAttributes<HTMLDivElement> & {
  /** Breathing room (px) between the status bar and the stuck row. Rows that
      carry their own vertical room (CategoryPageHeader's 57px row) use 0. */
  gap?: number;
};

const supportsObserver = typeof window !== 'undefined' && 'IntersectionObserver' in window;

export const StickyHeaderRow = ({
  gap = 8, className = '', style, children, ...rest
}: Props) => {
  const { shouldExpandForWeb } = useResponsive();
  const sentinelRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const enabled = !shouldExpandForWeb;

  // The scroll-edge layer must span the full viewport width even though the
  // row sits inside the page's side padding: measure the row's offset.
  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!enabled || !row) return undefined;
    const measure = () => {
      const rect = row.getBoundingClientRect();
      row.style.setProperty('--shr-left', `${-rect.left}px`);
      row.style.setProperty('--shr-vw', `${document.documentElement.clientWidth}px`);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [enabled]);

  // The row is stuck once its natural position has scrolled above its sticky
  // top. The sentinel is a zero-height block just above the row, nudged with
  // position: relative (which never affects layout) so that it sits exactly
  // stickyTop above the row's natural top. It leaving the top of the
  // viewport then means "the row is stuck and content is passing under it".
  const [shift, setShift] = useState(0);
  useLayoutEffect(() => {
    const row = rowRef.current;
    const sentinel = sentinelRef.current;
    if (!enabled || !row || !sentinel) return;
    const stickyTop = parseFloat(getComputedStyle(row).top) || 0;
    const offset = row.getBoundingClientRect().top - sentinel.getBoundingClientRect().top;
    setShift(Math.round(offset - stickyTop));
  }, [enabled]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!enabled || !supportsObserver || !sentinel) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      setScrolled(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [enabled, shift]);

  if (!enabled) {
    return <div className={className} style={style} {...rest}>{children}</div>;
  }

  return (
    <>
      <div ref={sentinelRef} aria-hidden="true" style={{ height: 0, position: 'relative', top: shift }} />
      <div
        ref={rowRef}
        className={`shr ${className}`}
        data-scrolled={scrolled ? 'true' : 'false'}
        style={{ ...style, ['--shr-gap' as string]: `${gap}px` }}
        {...rest}
      >
        {children}
      </div>
    </>
  );
};

export default StickyHeaderRow;
